import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useT } from '@dia/i18n';
import { loadShell, type ContentDoc } from '@dia/content';
import { listRooms, listHouses, type House, type Room } from '@dia/db';
import { LangToggle, Screen } from '@dia/ui';
import { useAuth } from '../auth';
import { supabase } from '../supabase';
import { Accordion } from './parts/Accordion';
import { BackLink } from './parts/BackLink';
import { Prose } from './parts/Prose';
import { RoomCardRow } from './parts/RoomCardRow';
import { bodyFor } from './logic/bilingual';
import { splitH2Sections } from './logic/guideSections';

const content = loadShell('guest');

/** Houses whose bedrooms the guide shows as room cards (the eight claimable bedrooms live here). */
const ROOM_CARD_HOUSES = new Set(['hacienda', 'bungalow']);

/**
 * One page of the House Guide, addressed by slug: `/casas/hacienda`,
 * `/casas/jacuzzi`, `/casas/incluido`. The QR code in each house opens exactly
 * this, so it has to work for a guest standing in the doorway with no signal
 * and no sign in.
 *
 * Two content sections feed this one route: `houses` (one page per house or
 * space) and, for the two topics that are stay-specific rather than
 * house-specific, `stay` ("What is included", tier aware, and "Crew and quiet
 * hours"). Wi-Fi, check-out and house rules are pinned cards on `/casas`
 * itself (docs/GUEST-REDESIGN.md Section 5) and are not pages here.
 *
 * A page with more than one `##` heading (How the island works) renders as
 * sections that open and close. Everything else reads as plain prose.
 */
export function HouseGuide() {
  const { slug = '' } = useParams();
  const { t, lang } = useT();
  const { session } = useAuth();

  const houseDoc = content.section('houses').find((p) => p.slug === slug);
  const tier = session?.stay.tier ?? null;
  const doc = houseDoc ?? extraDoc(slug, tier);
  const needsSignInForTier = !doc && slug === 'incluido' && !session;

  const [rooms, setRooms] = useState<Room[]>([]);
  useEffect(() => {
    if (!ROOM_CARD_HOUSES.has(slug)) {
      setRooms([]);
      return;
    }
    let live = true;
    (async () => {
      const [houses, allRooms] = await Promise.all([listHouses(supabase), listRooms(supabase)]);
      const house = houses.find((h: House) => h.slug === slug);
      if (!house || !live) return;
      setRooms(allRooms.filter((r) => r.house_id === house.id && r.guest_facing));
    })().catch(() => undefined);
    return () => {
      live = false;
    };
  }, [slug]);

  if (!doc) {
    return (
      <Screen title={t('guest.houses.title')} actions={<LangToggle />}>
        <BackLink to="/casas" label={t('guest.houses.all')} />
        <p className="text-inkMuted mt-2">
          {needsSignInForTier ? t('guest.guide.included_sign_in') : t('guest.houses.not_found')}
        </p>
        {needsSignInForTier && (
          <Link to="/entrar" className="text-accent underline underline-offset-4 min-h-tap inline-flex items-center mt-2">
            {t('guest.arrive.sign_in')}
          </Link>
        )}
      </Screen>
    );
  }

  const sections = splitH2Sections(bodyFor(doc.body, lang));
  const showAccordion = sections.length > 1;

  return (
    <Screen actions={<LangToggle />}>
      <BackLink to="/casas" label={t('guest.houses.all')} />
      <div className="mt-2 flex flex-col gap-8">
        <article>
          <h1 className="font-display text-ink text-[1.5em] leading-snug">{doc.title[lang]}</h1>
          {doc.lede && <p className="text-inkFaint text-caption mt-1 mb-4">{doc.lede[lang]}</p>}
          {showAccordion ? <Accordion sections={sections} /> : <Prose doc={doc} />}
        </article>

        {rooms.length > 0 && (
          <section aria-labelledby="rooms" className="border-t border-line pt-6">
            <h2 id="rooms" className="font-display text-[1.15em] mb-3">
              {t('guest.guide.rooms_heading')}
            </h2>
            <RoomCardRow rooms={rooms} stayId={session?.stay.id ?? null} />
          </section>
        )}

        {!session && (
          <Link to="/entrar" className="text-accent underline underline-offset-4 min-h-tap inline-flex items-center">
            {t('guest.arrive.sign_in')}
          </Link>
        )}
      </div>
    </Screen>
  );
}

/**
 * The two `stay` section topics that are pages in the guide rather than pinned
 * cards. "incluido" needs the guest's own tier; signed out, there is no page
 * to show, only a nudge to sign in.
 */
function extraDoc(slug: string, tier: 'all_inclusive' | 'standard' | null): ContentDoc | undefined {
  const stayDocs = content.section('stay');
  if (slug === 'incluido') {
    if (!tier) return undefined;
    return stayDocs.find((d) => d.tiers.includes(tier));
  }
  if (slug === 'crew-and-quiet') {
    return stayDocs.find((d) => d.slug === 'crew-and-quiet');
  }
  return undefined;
}
